import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const { t } = useLanguage();
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  
  const sessionId = searchParams.get('session_id');
  const method = searchParams.get('method');
  
  useEffect(() => {
    // Clear cart on success page load
    clearCart();
    
    // If coming from Stripe, poll for payment status
    if (sessionId) {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) {
          setPaymentStatus('timeout');
          return;
        }
        
        try {
          const response = await axios.get(`${API}/payments/status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            setPaymentStatus('success');
          } else if (response.data.status === 'expired') {
            setPaymentStatus('expired');
          } else {
            // Continue polling
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        } catch (error) {
          // Error handled silently
          setPaymentStatus('error');
        }
      };
      
      pollStatus();
    } else if (method === 'cash' || method === 'paypal') {
      setPaymentStatus('success');
    }
  }, [sessionId, method, clearCart]);
  
  return (
    <div className="min-h-screen pt-20 lg:pt-24 flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-lg mx-auto px-4 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', damping: 15 }}
          className="w-24 h-24 rounded-full bg-secondary mx-auto flex items-center justify-center mb-6"
        >
          {paymentStatus === 'checking' ? (
            <Package className="w-12 h-12 text-secondary_hover animate-pulse" />
          ) : paymentStatus === 'success' ? (
            <CheckCircle className="w-12 h-12 text-secondary_hover" />
          ) : (
            <Package className="w-12 h-12 text-secondary_hover" />
          )}
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {paymentStatus === 'checking' ? (
            <>
              <h1 className="text-3xl font-bold font-heading text-text_primary mb-4">
                Vérification du paiement...
              </h1>
              <p className="text-text_secondary mb-8">
                Nous vérifions votre paiement, veuillez patienter.
              </p>
            </>
          ) : paymentStatus === 'success' ? (
            <>
              <h1 className="text-3xl font-bold font-heading text-text_primary mb-4" data-testid="success-title">
                {t('orderSuccess')}
              </h1>
              <p className="text-text_secondary mb-8">
                {t('orderSuccessMessage')}
              </p>
              {method === 'cash' && (
                <div className="bg-accent/20 p-4 rounded-xl mb-8">
                  <p className="text-sm text-text_primary">
                    <strong>Paiement à la livraison</strong> - Préparez le montant exact en espèces.
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold font-heading text-text_primary mb-4">
                Une erreur est survenue
              </h1>
              <p className="text-text_secondary mb-8">
                Votre paiement n'a pas pu être vérifié. Contactez-nous pour assistance.
              </p>
            </>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary_hover transition-all"
              data-testid="back-to-home-button"
            >
              {t('backToHome')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/products"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-secondary/50 text-text_primary font-semibold rounded-full hover:bg-secondary transition-all"
            >
              {t('continueShopping')}
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
